/*
 * Adding to the bag from the collection grid.
 *
 * One place for the cart call, used by both the card's own button
 * (single-variant products) and the variant picker (everything else), so there
 * is exactly one cart pipeline on this page.
 *
 * It is Dawn's pipeline, not a new one: post to routes.cart_add_url, ask for
 * the sections the mounted cart element wants, publish cart-update, then hand
 * the response to that element's renderContents().
 */

/**
 * @param {Object} options
 * @param {number|string} options.variantId
 * @param {number} [options.quantity]
 * @param {HTMLElement} [options.opener] element the cart should return focus to
 * @returns {Promise<Object>} the cart response
 */
window.tyohAddToBag = async function tyohAddToBag({ variantId, quantity = 1, opener }) {
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const body = { items: [{ id: Number(variantId), quantity }] };

  if (cart) {
    body.sections = cart.getSectionsToRender().map((section) => section.id);
    body.sections_url = window.location.pathname;
    if (opener) cart.setActiveElement?.(opener);
  }

  const response = await fetch(routes.cart_add_url, {
    ...fetchConfig('javascript'),
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (data.status) throw new Error(data.description || data.message || window.cartStrings?.error);

  // Added successfully. A rendering failure past this point must not look like
  // a failed add, so it is logged rather than surfaced.
  try {
    if (!cart) {
      window.location = routes.cart_url;
      return data;
    }
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'card-add', cartData: data });
    // An `items` add returns no top-level `key`, which renderContents needs.
    const payload =
      !data.key && Array.isArray(data.items) && data.items.length
        ? { ...data, key: data.items[0].key }
        : data;
    cart.renderContents(payload);
  } catch (error) {
    /*
      The item is already in the cart at this point. Logging alone left the bag
      count stale, and a shopper who still sees Bag (0) adds the thing a second
      time — so the failure has to correct itself, not just be recorded.
      product-form.js has always done this; the card buttons had not.
    */
    console.error('[card-add] added to the bag, but the cart UI failed to update', error);
    cart.refreshFromServer?.();
  }

  return data;
};

/*
 * The bar on a single-variant card. Anything with options opens the picker
 * instead, so this never has to guess a variant.
 */
if (!customElements.get('tyoh-card-add')) {
  customElements.define(
    'tyoh-card-add',
    class TyohCardAdd extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-tcard-add]');
        if (!this.button) return;
        this.button.addEventListener('click', this.onClick.bind(this));
      }

      setLoading(isLoading) {
        this.button.classList.toggle('is-loading', isLoading);
        this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      async onClick(event) {
        // The card's title link is stretched over the whole card; without this
        // the click would also navigate to the product page.
        event.preventDefault();
        event.stopPropagation();

        if (this.button.getAttribute('aria-disabled') === 'true') return;

        const variantId = this.button.dataset.variantId;
        if (!variantId) return;

        this.setLoading(true);
        try {
          await window.tyohAddToBag({ variantId, opener: this.button });
        } catch (error) {
          console.error(error);
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
